// src/pages/tenant/TenantListPage.tsx
// Tenant list — "+ New" opens TenantCreateDialog in place instead of routing
// to /tenants/new. See docs/ux/14-forms.md.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenantTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { TenantCreateDialog } from '@/features/tenant/TenantCreateDialog';

/**
 * Tenant list page.
 */
export const TenantListPage = observer(() => {
  const store = useTenantTenant();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    store.ListTenants({
      pagination: { cursor: '', limit: 100, direction: 'forward' },
      sort: [],
      date_range: {} as any,
      filters: {} as any,
    }).catch(() => {});
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const filtered = search.trim()
    ? store.items.filter((item: any) =>
        (item.name ?? '').toLowerCase().includes(search.trim().toLowerCase()) ||
        (item.slug ?? '').toLowerCase().includes(search.trim().toLowerCase())
      )
    : store.items;

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('tenant.list')}</h1>
        <Button className="sm:ml-auto" onClick={() => setShowCreate(true)}>{t('tenant.create')}</Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tenant.name')}</TableHead>
              <TableHead>{t('tenant.slug')}</TableHead>
              <TableHead>{t('tenant.description')}</TableHead>
              <TableHead>{t('tenant.region')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableEmpty colSpan={5} message={t('tenant.noItems')} />
            ) : (
              filtered.map((item: any) => {
                const id = item.entity?.id || item.id;
                return (
                  <TableRow key={id || Math.random()}>
                    <TableCell>
                      <Link to={`/tenants/${id}`} className="font-medium text-foreground hover:underline">
                        {String(item.name ?? '')}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">{String(item.slug ?? '')}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">{String(item.description ?? '')}</span>
                    </TableCell>
                    <TableCell>
                      {String(item.region ?? '')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/tenants/${id}`}>
                          <Button variant="ghost" size="sm">{t('common.view')}</Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => {
                            if (confirm(t('tenant.deleteConfirm'))) {
                              store.DeleteTenant(id);
                            }
                          }}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <TenantCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          store.ListTenants({
            pagination: { cursor: '', limit: 100, direction: 'forward' },
            sort: [],
            date_range: {} as any,
            filters: {} as any,
          }).catch(() => {});
          if (id) navigate(`/tenants/${id}`);
        }}
      />
    </div>
  );
});

export default TenantListPage;
